import { ClipboardModule } from '@angular/cdk/clipboard';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { TextFieldModule } from '@angular/cdk/text-field';
import { CommonModule, DatePipe, registerLocaleData } from '@angular/common';
import { HttpClientModule, provideHttpClient, withInterceptors } from '@angular/common/http';
import localeFrExtra from '@angular/common/locales/extra/fr';
import localeFr from '@angular/common/locales/fr';
import { CSP_NONCE, CUSTOM_ELEMENTS_SCHEMA, DEFAULT_CURRENCY_CODE, LOCALE_ID, NgModule } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { MatBadgeModule } from '@angular/material/badge';

import { MatButtonToggleModule } from '@angular/material/button-toggle';

import { MAT_RIPPLE_GLOBAL_OPTIONS, MatNativeDateModule, MatPseudoCheckboxModule, RippleGlobalOptions } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';

import { MatExpansionModule } from '@angular/material/expansion';

import { MatIconModule } from '@angular/material/icon';

import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';

import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ServiceWorkerModule, SwRegistrationOptions } from '@angular/service-worker';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { environment } from 'src/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { DialyseComponent } from './components/dialyse/dialyse.component';
import { InterventionListComponent } from './components/intervention-list/intervention-list.component';
import { InterventionComponent } from './components/intervention/intervention.component';
import { NavMenuComponent } from './components/nav-menu/nav-menu.component';
import { PointageComponent } from './components/pointage/pointage.component';
import { RedirectComponent } from './components/redirect/redirect.component';
import { WeeklyListComponent } from './components/weekly-list/weekly-list.component';
import { ZoneComponent } from './components/zone/zone.component';
import { ErrorComponent } from './error.component';
import { CoreData } from './tools/core-data/core-data';
import { DragDropDirective } from './tools/directive/drag-drop-directive';
import { NumberDirective } from './tools/directive/number-only.directive';
import { PreventDoubleClickDirective } from './tools/directive/prevent-double-click.directive';
import { LoginGuard } from './tools/login-guard';
import { ConfirmBoxDialog } from './tools/ui/confirm-box/confirm-box.component';
import { ConsommableBoxDialog } from './tools/ui/consommable-box/consommable-box';
import { CustomSnackBarComponent } from './tools/ui/custom-snackbar/custom-snackbar.component';
import { MultipleFileUploadBoxDialog } from './tools/ui/mutilple-file-upload-box/multiple-file-upload-box';
import { PointageBoxDialog } from './tools/ui/pointage-box/pointage-box';
import { EndoscopeComponent } from './components/endoscope/endoscope.component';
import { LoadingTask } from './tools/loading-task';
import { secureApiInterceptor } from './tools/secure-api.interceptor';
import { NgCircleProgressModule } from 'ng-circle-progress';
import { AppareilsComponent } from './components/appareils/appareils.component';

const globalRippleConfig: RippleGlobalOptions = {
    disabled: true,
    animation: {
        enterDuration: 300,
        exitDuration: 0
    }
};
const nonce = (
    document.querySelector('meta[name="CSP_NONCE"]') as HTMLMetaElement
)?.content;

@NgModule({
    declarations: [AppComponent],
    imports: [
        BrowserModule,
        AppRoutingModule,
        HttpClientModule,
        CommonModule,
        FormsModule,
        BrowserModule,
        ReactiveFormsModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        ClipboardModule,
        MatTableModule,
        MatSnackBarModule,
        MatFormFieldModule,
        MatExpansionModule,
        MatInputModule,
        MatButtonModule,
        MatProgressBarModule,
        MatTooltipModule,
        MatDialogModule,
        MatIconModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatPaginatorModule,
        MatAutocompleteModule,
        MatSelectModule,
        MatSortModule,
        MatChipsModule,
        MatSlideToggleModule,
        MatButtonToggleModule,
        MatStepperModule,
        MatListModule,
        MatBadgeModule,
        MatMenuModule,
        TextFieldModule,
        ScrollingModule,
        MatPseudoCheckboxModule,
        FlexLayoutModule,
        ServiceWorkerModule.register('ngsw-worker.js'),
        NgxMaterialTimepickerModule,
        CustomSnackBarComponent,
        NavMenuComponent,
        ErrorComponent,
        PreventDoubleClickDirective,
        NumberDirective,
        DragDropDirective,
        ConfirmBoxDialog,
        InterventionListComponent,
        WeeklyListComponent,
        InterventionComponent,
        MultipleFileUploadBoxDialog,
        RedirectComponent,
        PointageComponent,
        PointageBoxDialog,
        ConsommableBoxDialog,
        ZoneComponent,
        DialyseComponent,
        EndoscopeComponent,
        NgCircleProgressModule.forRoot(),
        AppareilsComponent
    ],
    providers: [
        { provide: LOCALE_ID, useValue: 'fr-FR' },
        // { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { disabled: true } },
        { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
        { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: globalRippleConfig },
        {
            provide: SwRegistrationOptions,
            useFactory: () => ({ enabled: environment.production }),
        },
        CoreData,
        LoadingTask,
        LoginGuard,
        DatePipe,
        provideHttpClient(withInterceptors([secureApiInterceptor])),
        {
            provide: CSP_NONCE,
            useValue: nonce,
        },
    ],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule { }

registerLocaleData(localeFr, 'fr', localeFrExtra);
